import { Button, Container, Label, SelectInput } from '@playcanvas/pcui';

import { ElementType } from '../element';
import { Events } from '../events';
import type { HelperPoint } from '../helper-point';
import type { Scene } from '../scene';
import type { UnitSystem } from '../unit-system';
import type { VehicleMotionController } from '../vehicle-motion-controller';
import { computeVelocityTrackFromFrames, getSpeedAtFrame } from '../velocity';

type SpeedWidgetConfig = {
    id: string;
    targetRef: string | null;
};

const getScene = (): Scene | null => {
    return ((window as any).scene as Scene) ?? null;
};

const makeWidgetId = () => {
    try {
        return crypto.randomUUID();
    } catch {
        return `speed-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    }
};

const getTargetRef = (helper: HelperPoint | null): string | null => {
    if (!helper) return null;
    const name = String((helper as any).name ?? '').trim();
    if (!name) return null;

    const vehicleId = (helper as any).vehicleId;
    if (vehicleId !== null && vehicleId !== undefined && String(vehicleId).length) {
        return `${vehicleId}:${name}`;
    }

    return name;
};

const resolveTargetRef = (scene: Scene, targetRef: string | null): HelperPoint | null => {
    if (!scene || !targetRef) return null;

    const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];
    const trimmed = String(targetRef).trim();
    if (!trimmed) return null;

    const idx = trimmed.indexOf(':');
    if (idx > 0) {
        const vehicleId = trimmed.slice(0, idx);
        const name = trimmed.slice(idx + 1);
        const match = helpers.find(h => String((h as any).vehicleId ?? '') === vehicleId && String((h as any).name ?? '') === name);
        if (match) return match;
    }

    // Fallback: name only.
    return helpers.find(h => String((h as any).name ?? '') === trimmed) ?? null;
};

const findControllerForHelper = (scene: Scene, helper: HelperPoint | null): VehicleMotionController | null => {
    if (!scene || !helper) return null;

    // Fast path: controller directly on this helper.
    const direct = (helper as any).vehicleMotionController as VehicleMotionController | undefined;
    if (direct) return direct;

    // Walk up logical element chain.
    let cur: any = helper;
    while (cur) {
        const c = cur.vehicleMotionController as VehicleMotionController | undefined;
        if (c) return c;
        cur = cur.parentElement;
    }

    // Fallback: find any *_root helper with a controller and matching vehicleId.
    const vid = (helper as any).vehicleId;
    if (vid !== null && vid !== undefined) {
        const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];
        for (const h of helpers) {
            const name = String((h as any).name ?? '');
            if (!name.endsWith('_root')) continue;
            if (String((h as any).vehicleId ?? '') !== String(vid)) continue;
            const c = (h as any).vehicleMotionController as VehicleMotionController | undefined;
            if (c) return c;
        }
    }

    return null;
};

class SpeedWidget extends Container {
    widgetId: string;

    private readonly events: Events;

    private readonly targetSelect: SelectInput;

    private readonly speedLabel: Label;

    private readonly removeButton: Button;

    private targetRef: string | null = null;

    private cachedFps: number | null = null;

    private cachedFrames: number[] | null = null;

    private cachedPositions: number[] | null = null;

    private cachedSpeedsMps: number[] | null = null;

    constructor(events: Events, widgetId: string, initialTargetRef: string | null, onRemove: (id: string) => void) {
        super({
            class: 'speed-widget'
        });

        this.events = events;
        this.widgetId = widgetId;

        this.targetSelect = new SelectInput({
            class: 'speed-widget-target',
            defaultValue: ''
        });

        this.speedLabel = new Label({
            class: 'speed-widget-readout',
            text: '--'
        });

        this.removeButton = new Button({
            class: ['button', 'speed-widget-remove'],
            text: '×'
        });

        this.removeButton.on('click', () => onRemove(this.widgetId));

        this.append(this.targetSelect);
        this.append(this.speedLabel);
        this.append(this.removeButton);

        // Minimal inline styling to keep scope isolated (no SCSS changes).
        this.dom.style.display = 'flex';
        this.dom.style.alignItems = 'center';
        this.dom.style.gap = '6px';
        this.dom.style.padding = '6px 8px';
        this.dom.style.borderRadius = '6px';
        this.dom.style.background = 'rgba(0,0,0,0.45)';
        this.dom.style.backdropFilter = 'blur(4px)';
        this.dom.style.color = 'white';

        this.speedLabel.dom.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
        this.speedLabel.dom.style.fontSize = '14px';
        this.speedLabel.dom.style.minWidth = '80px';
        this.speedLabel.dom.style.textAlign = 'right';

        this.targetSelect.dom.style.minWidth = '160px';

        this.setTargetRef(initialTargetRef);

        this.targetSelect.on('change', (value: string) => {
            const next = String(value ?? '').trim();
            this.setTargetRef(next ? next : null);
            this.events.fire('speedhud.changed');
        });
    }

    serialize(): SpeedWidgetConfig {
        return {
            id: this.widgetId,
            targetRef: this.targetRef
        };
    }

    setTargetRef(targetRef: string | null) {
        this.targetRef = targetRef && String(targetRef).trim() ? String(targetRef).trim() : null;

        // Clear cache whenever target changes.
        this.cachedFrames = null;
        this.cachedPositions = null;
        this.cachedSpeedsMps = null;
        this.cachedFps = null;
    }

    rebuildOptions(scene: Scene) {
        const helpers = (scene.getElementsByType(ElementType.helper) as HelperPoint[])
        .filter(h => !!(h as any).vehicleMotionController)
        .filter(h => String((h as any).name ?? '').endsWith('_root'))
        .sort((a, b) => String((a as any).name ?? '').localeCompare(String((b as any).name ?? '')));

        const options = [{ v: '', t: 'Unassigned' }].concat(
            helpers.map((h) => {
                const ref = getTargetRef(h) ?? String((h as any).name ?? '');
                const t = String((h as any).name ?? ref);
                return { v: ref, t };
            })
        );

        // pcui SelectInput wants string values.
        this.targetSelect.options = options as any;

        const current = this.targetRef ?? '';
        this.targetSelect.value = current;
    }

    invalidateFps() {
        this.cachedFps = null;
        this.cachedSpeedsMps = null;
    }

    updateAtFrame(frame: number) {
        const scene = getScene();
        if (!scene) {
            this.speedLabel.text = '--';
            return;
        }

        const helper = resolveTargetRef(scene, this.targetRef);
        const controller = findControllerForHelper(scene, helper);
        if (!controller) {
            this.speedLabel.text = '--';
            return;
        }

        const unitSystem = this.events.invoke('units.system') as UnitSystem | undefined;
        const fps = this.events.invoke('timeline.frameRate') as number;
        const safeFps = (typeof fps === 'number' && Number.isFinite(fps) && fps > 0) ? fps : 30;

        if (this.cachedFps !== safeFps || !this.cachedFrames || !this.cachedPositions || !this.cachedSpeedsMps) {
            const tracks = controller.getTracks();
            const frames = tracks.body.frames;
            const positions = tracks.body.positions;

            this.cachedFrames = frames;
            this.cachedPositions = positions;
            this.cachedFps = safeFps;
            this.cachedSpeedsMps = computeVelocityTrackFromFrames(positions, frames, safeFps, 3);
        }

        const speedMps = getSpeedAtFrame(this.cachedPositions, this.cachedFrames, this.cachedSpeedsMps, frame);

        if (unitSystem && typeof unitSystem.formatSpeed === 'function') {
            this.speedLabel.text = unitSystem.formatSpeed(speedMps);
        } else {
            this.speedLabel.text = `${speedMps.toFixed(2)} m/s`;
        }
    }
}

class SpeedHud extends Container {
    private readonly events: Events;

    private readonly addButton: Button;

    private readonly widgetsContainer: Container;

    private widgets: SpeedWidget[] = [];

    private lastFrame: number = 0;

    constructor(events: Events) {
        super({
            id: 'speed-hud'
        });

        this.events = events;

        this.dom.style.position = 'absolute';
        this.dom.style.top = '10px';
        this.dom.style.left = '10px';
        this.dom.style.zIndex = '20';
        this.dom.style.pointerEvents = 'auto';

        this.addButton = new Button({
            class: ['button', 'speedhud-add'],
            text: '+'
        });
        this.addButton.dom.style.width = '28px';
        this.addButton.dom.style.height = '28px';
        this.addButton.dom.style.padding = '0';

        this.widgetsContainer = new Container({
            class: 'speedhud-widgets'
        });
        this.widgetsContainer.dom.style.display = 'flex';
        this.widgetsContainer.dom.style.flexDirection = 'column';
        this.widgetsContainer.dom.style.gap = '6px';
        this.widgetsContainer.dom.style.marginTop = '6px';

        this.append(this.addButton);
        this.append(this.widgetsContainer);

        const removeWidget = (id: string) => {
            const idx = this.widgets.findIndex(w => w.widgetId === id);
            if (idx === -1) return;

            const w = this.widgets[idx];
            this.widgets.splice(idx, 1);
            this.widgetsContainer.remove(w);
            this.events.fire('speedhud.remove', id);
            this.events.fire('speedhud.changed');
        };

        const addWidget = (config?: Partial<SpeedWidgetConfig>) => {
            const id = config?.id ?? makeWidgetId();
            const targetRef = config?.targetRef ?? null;
            const widget = new SpeedWidget(this.events, id, targetRef, removeWidget);
            this.widgets.push(widget);
            this.widgetsContainer.append(widget);

            const scene = getScene();
            if (scene) {
                widget.rebuildOptions(scene);
                widget.updateAtFrame(this.lastFrame);
            }

            this.events.fire('speedhud.add', id);
            this.events.fire('speedhud.changed');
        };

        this.addButton.on('click', () => {
            addWidget({});
        });

        // Persistence hooks (NSV manifest).
        events.function('speedhud.widgets', () => {
            return this.widgets.map(w => w.serialize());
        });

        events.on('speedhud.setWidgets', (configs: SpeedWidgetConfig[]) => {
            // Replace all widgets.
            this.widgets.forEach((w) => {
                try {
                    this.widgetsContainer.remove(w);
                } catch {
                    // ignore
                }
            });
            this.widgets = [];

            if (Array.isArray(configs)) {
                configs.forEach(cfg => addWidget(cfg));
            }

            this.events.fire('speedhud.changed');
        });

        // Keep options in sync when helpers change.
        events.on('scene.elementAdded', (el: any) => {
            if (el?.type === ElementType.helper) this.rebuildAllOptions();
        });
        events.on('scene.elementRemoved', (el: any) => {
            if (el?.type === ElementType.helper) this.rebuildAllOptions();
        });
        events.on('hierarchy.changed', () => this.rebuildAllOptions());

        // Update on timeline movement.
        events.on('timeline.frame', (frame: number) => {
            this.lastFrame = frame;
            this.updateAll();
        });
        events.on('timeline.time', (time: number) => {
            this.lastFrame = time;
            this.updateAll();
        });
        events.on('timeline.frameRate', () => {
            // Frame rate affects m/s conversion.
            for (const w of this.widgets) w.invalidateFps();
            this.updateAll();
        });
        events.on('units.changed', () => {
            this.updateAll();
        });

        // First build.
        this.rebuildAllOptions();
        this.updateAll();
    }

    private rebuildAllOptions() {
        const scene = getScene();
        if (!scene) return;
        for (const w of this.widgets) {
            w.rebuildOptions(scene);
        }
    }

    private updateAll() {
        for (const w of this.widgets) {
            w.updateAtFrame(this.lastFrame);
        }
    }
}

export { SpeedHud };
export type { SpeedWidgetConfig };
